/**
 * QuickStats - Credit burn, team availability, and uptime panels
 * CHANGED: Team availability now polls /api/agents/status for real data
 * instead of hardcoded "ACTIVE" strings.
 * OpenClaw agents: no changes needed.
 */
'use client';

import React from 'react';
import { Clock, Users, CreditCard, TrendingUp } from 'lucide-react';
import { usePolling } from '@/lib/usePolling';

interface StatsData { hrs: string }
interface FinanceData { daily_burn: number; monthly_projected: number; event_count: number }
interface AgentStatus { name: string; status: string; activeTasks: number }
interface AgentStatusResponse { agents: AgentStatus[] }
interface FinancialSummary {
  projection: { net_cash_projected_cents: number };
  metrics: { net_margin_pct: number };
  cash: {
    on_hand_cents: number;
    reserve_target_cents: number;
    critical_threshold_cents: number;
    projected_end_cents: number;
    status: 'ok' | 'warning' | 'critical';
  };
}

export const QuickStats = () => {
  const { data: stats } = usePolling<StatsData>({
    url: '/api/stats',
    intervalMs: 30000,
    initialValue: { hrs: '0.0' },
  });

  const { data: finance } = usePolling<FinanceData>({
    url: '/api/finance',
    intervalMs: 30000,
    initialValue: { daily_burn: 0, monthly_projected: 0, event_count: 0 },
  });

  const { data: agentData } = usePolling<AgentStatusResponse>({
    url: '/api/agents/status',
    intervalMs: 10000,
    initialValue: { agents: [] },
  });

  const { data: financials } = usePolling<FinancialSummary>({
    url: '/api/financials/summary',
    intervalMs: 60000,
    initialValue: {
      projection: { net_cash_projected_cents: 0 },
      metrics: { net_margin_pct: 0 },
      cash: { on_hand_cents: 0, reserve_target_cents: 0, critical_threshold_cents: 0, projected_end_cents: 0, status: 'ok' },
    },
  });

  const topAgents = agentData.agents
    .filter(a => a.name !== 'Historical')
    .slice(0, 4);

  return (
    <div className="space-y-6">
      <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-6 backdrop-blur-xl">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <CreditCard className="w-4 h-4 text-zinc-500" />
            <p className="text-[10px] font-black text-zinc-400 uppercase tracking-widest">Credit Burn</p>
          </div>
          <span className="text-[10px] text-zinc-500 font-mono">24H</span>
        </div>
        <div className="flex items-baseline gap-2 mb-2">
          <span className="text-2xl font-black text-white tracking-tighter tabular-nums">${finance.daily_burn}</span>
          <span className="text-[10px] text-zinc-500 font-mono uppercase">daily average</span>
        </div>
        <div className="pt-4 border-t border-white/5 flex justify-between items-center text-[10px] font-mono">
            <span className="text-zinc-500">MTH PROJECTED</span>
            <span className="text-white tabular-nums">${finance.monthly_projected}</span>
        </div>
        {finance.event_count > 0 && (
          <div className="pt-2 flex justify-between items-center text-[10px] font-mono">
            <span className="text-zinc-500">WORKFLOWS</span>
            <span className="text-zinc-400 tabular-nums">{finance.event_count}</span>
          </div>
        )}
      </div>

      <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-6 backdrop-blur-xl">
        <div className="flex items-center gap-3 mb-4">
          <TrendingUp className="w-4 h-4 text-zinc-500" />
          <p className="text-[10px] font-black text-zinc-400 uppercase tracking-widest">Financial Pulse</p>
        </div>
        <div className="flex items-baseline gap-2 mb-2">
          <span className="text-2xl font-black text-white tracking-tighter tabular-nums">
            {(financials.metrics.net_margin_pct ?? 0).toFixed(1)}%
          </span>
          <span className="text-[10px] text-zinc-500 font-mono uppercase">net margin</span>
        </div>
        <div className="pt-4 border-t border-white/5 flex justify-between items-center text-[10px] font-mono">
          <span className="text-zinc-500">PROJECTED CASH</span>
          <span className={`tabular-nums ${financials.projection.net_cash_projected_cents < 0 ? 'text-rose-400' : 'text-emerald-400'}`}>
            ${(financials.projection.net_cash_projected_cents / 100).toFixed(0)}
          </span>
        </div>
      </div>

      <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-6 backdrop-blur-xl">
        <div className="flex items-center gap-3 mb-4">
          <TrendingUp className="w-4 h-4 text-zinc-500" />
          <p className="text-[10px] font-black text-zinc-400 uppercase tracking-widest">Cash Risk</p>
        </div>
        <div className="flex items-baseline gap-2 mb-2">
          <span className="text-2xl font-black text-white tracking-tighter tabular-nums">
            ${(financials.cash.projected_end_cents / 100).toFixed(0)}
          </span>
          <span className="text-[10px] text-zinc-500 font-mono uppercase">projected end</span>
        </div>
        <div className="pt-4 border-t border-white/5 flex justify-between items-center text-[10px] font-mono">
          <span className="text-zinc-500">ON HAND</span>
          <span className="text-white tabular-nums">${(financials.cash.on_hand_cents / 100).toFixed(0)}</span>
        </div>
        <div className="pt-2 flex justify-between items-center text-[10px] font-mono">
          <span className="text-zinc-500">RESERVE TARGET</span>
          <span className="text-white tabular-nums">${(financials.cash.reserve_target_cents / 100).toFixed(0)}</span>
        </div>
        <div className="pt-2 flex justify-between items-center text-[10px] font-mono">
          <span className="text-zinc-500">STATUS</span>
          <span
            className={`${
              financials.cash.status === 'critical'
                ? 'text-rose-400'
                : financials.cash.status === 'warning'
                  ? 'text-amber-400'
                  : 'text-emerald-400'
            }`}
          >
            {financials.cash.status.toUpperCase()}
          </span>
        </div>
      </div>

      <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-6 backdrop-blur-xl">
        <div className="flex items-center gap-3 mb-6">
          <Users className="w-4 h-4 text-zinc-500" />
          <p className="text-[10px] font-black text-zinc-400 uppercase tracking-widest">Team Availability</p>
        </div>
        <div className="space-y-3">
          {topAgents.length > 0 ? topAgents.map(agent => (
            <div key={agent.name} className="flex justify-between items-center text-xs">
              <span className="text-zinc-500 italic">{agent.name}</span>
              <span className={`font-mono ${agent.status === 'ACTIVE' ? 'text-emerald-400' : 'text-zinc-500'}`}>
                {agent.status}
                {agent.activeTasks > 0 && <span className="text-zinc-500 ml-1">({agent.activeTasks})</span>}
              </span>
            </div>
          )) : (
            <div className="text-zinc-500 text-[10px] font-mono text-center py-2">Loading agents...</div>
          )}
        </div>
      </div>

      <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-6 backdrop-blur-xl">
        <div className="flex items-center gap-3 mb-2">
          <Clock className="w-4 h-4 text-zinc-500" />
          <p className="text-[10px] font-black text-zinc-400 uppercase tracking-widest">Continuity</p>
        </div>
        <div className="text-2xl font-black text-white tracking-tighter tabular-nums">{stats.hrs} hrs</div>
        <div className="text-[10px] text-zinc-500 font-mono uppercase">Dashboard Uptime</div>
      </div>
    </div>
  );
};
